import globalStyles from "../assets/styles/bootstrap.min.module.css";
import classes from "./LoginForm.module.css";
import { useRef } from "react";
import { signupAction, loginAction } from "../store/auth-actions";
import { useAppDispatch } from "../hooks/redux-hooks";
import isEmail from "../utils/string/isEmail";

import useInput from "../hooks/use-input";
import isPassword from "../utils/string/isPassword";
import areEqual from "../utils/string/areEqual";
import { useNavigate } from "react-router-dom";
import { LOGIN_RESPONSE } from "../types/user";
import isValidUserName from "../utils/string/isValidUserName";

const LOGIN_SUCCESS_RESPONSE = "Login successful!";

type SignupResponseStatus = "Registration Successful!";

export type LoginFormProps = {
  isLogin: boolean;
  toggleLoginSignup: (isLogin: boolean) => void;
};

const LoginForm = (props: LoginFormProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLogin, toggleLoginSignup } = props;
  const usernameInput = useRef<HTMLInputElement>(null);
  const emailInput = useRef<HTMLInputElement>(null);
  const passwordInput = useRef<HTMLInputElement>(null);
  const confirmPassword = useRef<HTMLInputElement>(null);

  const {
    value: emailValue,
    isValid: emailIsValid,
    hasError: emailHasError,
    valueChangeHandler: emailChangeHandler,
    inputBlurHandler: emailBlurHandler,
    reset: resetEmail,
  } = useInput(isEmail);

  const {
    value: userNameValue,
    isValid: userNameIsValid,
    hasError: userNameHasError,
    valueChangeHandler: userNameChangeHandler,
    inputBlurHandler: userNameBlurHandler,
    reset: resetUserName,
  } = useInput(isValidUserName);

  const {
    value: passwordValue,
    isValid: passwordIsValid,
    hasError: passwordHasError,
    valueChangeHandler: passwordChangeHandler,
    inputBlurHandler: passwordBlurHandler,
    reset: resetPassword,
  } = useInput(isPassword);

  const passwordsMatch = (confirmPasswordValue: string): boolean => {
    return areEqual(passwordValue, confirmPasswordValue);
  };

  const {
    value: confirmPasswordValue,
    isValid: confirmPasswordIsValid,
    hasError: confirmPasswordHasError,
    valueChangeHandler: confirmPasswordChangeHandler,
    inputBlurHandler: confirmPasswordBlurHandler,
    reset: resetconfirmPassword,
  } = useInput(passwordsMatch);

  const formIsValid =
    userNameIsValid &&
    passwordIsValid &&
    (!isLogin
      ? confirmPasswordIsValid &&
        emailIsValid &&
        areEqual(passwordValue, confirmPasswordValue)
      : true);

  const emailClasses = emailHasError ? "form-control invalid" : "form-control";
  const userNameClasses = userNameHasError
    ? "form-control invalid"
    : "form-control";
  const passwordClasses = passwordHasError
    ? "form-control invalid"
    : "form-control";
  const confirmPasswordClasses = confirmPasswordHasError
    ? "form-control invalid"
    : "form-control";

  const authFormSubmitHandler = async (
    event: React.FormEvent
  ): Promise<void> => {
    event.preventDefault();

    try {
      if (isLogin) {
        const loginResponse = (await dispatch(
          loginAction({
            username: usernameInput?.current?.value,
            password: passwordInput?.current?.value,
          })
        )) as LOGIN_RESPONSE;
        window.alert(loginResponse);
        if (loginResponse === LOGIN_SUCCESS_RESPONSE) {
          navigate("/dashboard");
        }
      } else {
        const signupResponse = (await dispatch(
          signupAction({
            username: usernameInput?.current?.value,
            email: emailInput?.current?.value,
            fullName: "Temp Name",
            password: passwordInput?.current?.value,
          })
        )) as SignupResponseStatus;
        window.alert(signupResponse);
        toggleLoginSignup(isLogin);
      }
    } catch (err) {
      window.alert("Request failed.");
    }

    resetEmail();
    resetUserName();
    resetPassword();
    resetconfirmPassword();
  };

  return (
    <form onSubmit={authFormSubmitHandler}>
      <div
        className={`${globalStyles["form-group"]} ${classes["form-group"]} ${classes[userNameClasses]}`}
      >
        <label htmlFor="exampleUserName1">Username</label>
        <input
          type="text"
          className={`${globalStyles["form-control"]} ${classes["form-control"]} ${globalStyles["form-control-sm"]}`}
          // id="email-username"
          aria-describedby="userNameHelp"
          placeholder="alphnum of atleast 6 characters"
          ref={usernameInput}
          value={userNameValue}
          onChange={userNameChangeHandler}
          onBlur={userNameBlurHandler}
        />
        {userNameHasError && (
          <p className={classes["error-text"]}>
            Please enter a valid user name.
          </p>
        )}
      </div>
      {!isLogin && (
        <div
          className={`${globalStyles["form-group"]} ${classes["form-group"]} ${classes[emailClasses]}`}
        >
          <label htmlFor="exampleInputEmail1">Email address</label>
          <input
            type="text"
            className={`${globalStyles["form-control"]} ${classes["form-control"]} ${globalStyles["form-control-sm"]}`}
            // id="email-username"
            aria-describedby="emailHelp"
            placeholder="someone@yourmail.com"
            ref={emailInput}
            value={emailValue}
            onChange={emailChangeHandler}
            onBlur={emailBlurHandler}
          />
          {emailHasError && (
            <p className={classes["error-text"]}>
              Please enter a valid email address.
            </p>
          )}
        </div>
      )}
      <div
        className={`${globalStyles["form-group"]} ${classes["form-group"]} ${classes[passwordClasses]}`}
      >
        <label htmlFor="exampleInputPassword1">Password</label>
        <input
          type="password"
          name="password"
          value={passwordValue}
          onChange={passwordChangeHandler}
          onBlur={passwordBlurHandler}
          className={`${globalStyles["form-control"]}  ${globalStyles["form-control-sm"]} ${classes["form-control"]}`}
          // id="password"
          placeholder="&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;"
          ref={passwordInput}
        />
        {passwordHasError && (
          <p className={classes["error-text"]}>
            Please enter a valid password.
          </p>
        )}
      </div>
      {!isLogin && (
        <div
          className={`${globalStyles["form-group"]} ${classes["form-group"]}`}
        >
          <label htmlFor="exampleInputPassword1">Confirm Password</label>
          <input
            type="password"
            value={confirmPasswordValue}
            onChange={confirmPasswordChangeHandler}
            onBlur={confirmPasswordBlurHandler}
            className={`${globalStyles["form-control"]} ${globalStyles["form-control-sm"]} ${classes["form-control"]} ${classes[confirmPasswordClasses]}`}
            id="signup-confirm-password"
            placeholder="&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;"
            ref={confirmPassword}
          />
          {confirmPasswordHasError && (
            <p className={classes["error-text"]}>Passwords must match!.</p>
          )}
        </div>
      )}
      <a href="/#" id="login-link">
        <button
          disabled={!formIsValid}
          type="submit"
          className={`${globalStyles["btn"]} ${classes["btn"]} ${globalStyles["btn-primary"]} ${globalStyles["btn-block"]}`}
        >
          {isLogin ? "Log in" : "Sign up"}
        </button>
      </a>

      {isLogin && (
        <div className={classes["sign-up"]}>
          <a href="/#" id="forgot-link">
            Forgot Password?
          </a>
        </div>
      )}
    </form>
  );
};

export default LoginForm;
